import React from "react";
import { emptyLocale } from "./locale";

/** Parse block drag ID to path: "blk-2" → [2], "blk-2-1" → [2, 1] */
export function parsePath(id: string): number[] {
  return id.replace(/^blk-/, "").split("-").map(Number);
}

/** Create an empty block from config or fallback */
export function createEmptyBlock(type: string, config?: any[]): any {
  if (config) {
    const def = config.find((b: any) => b.type === type);
    if (def) {
      const block = JSON.parse(JSON.stringify(def.template));
      for (const field of def.fields || []) {
        if (field.localized) {
          block[field.key] = emptyLocale();
        }
      }
      return block;
    }
  }
  // Fallback when no config
  const el = emptyLocale();
  switch (type) {
    case "title": return { type: "title", text: { ...el } };
    case "text": return { type: "text", text: { ...el } };
    case "image": return { type: "image" };
    case "group": return { type: "group", content: [] };
    case "shopping_list": return { type: "shopping_list", name: { ...el }, items: [] };
    case "day": return { type: "day", title: { ...el }, meals: [] };
    case "card": return { type: "card", title: { ...el }, image: "" };
    case "tip": return { type: "tip", title: { ...el }, text: { ...el } };
    default: return { type, text: { ...el } };
  }
}

/** Get a short preview text from a block */
export function getBlockPreview(block: any, locale: string): string {
  const getText = (obj: any): string => {
    if (!obj) return "";
    if (typeof obj === "string") return obj;
    if (typeof obj === "object" && !Array.isArray(obj)) return obj[locale]?.trim() || obj.en?.trim() || "";
    return "";
  };
  const preview = getText(block.text) || getText(block.title) || getText(block.name);
  if (preview) {
    const clean = preview.replace(/^#+\s*/, "").replace(/\*\*/g, "");
    return clean.length > 40 ? clean.slice(0, 40) + "..." : clean;
  }
  if (Array.isArray(block.content)) return `${block.content.length} blocks`;
  if (block.imageRef || block.url) return block.imageRef || block.url;
  return block.type;
}

/** SVG icon per block type */
export function BlockIcon({ type }: { type: string }) {
  const s = { width: 14, height: 14, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 2 } as const;
  switch (type) {
    case "title":
      return React.createElement("svg", s, React.createElement("path", { d: "M6 4v16" }), React.createElement("path", { d: "M18 4v16" }), React.createElement("path", { d: "M6 12h12" }));
    case "text":
      return React.createElement("svg", s, React.createElement("path", { d: "M4 6h16" }), React.createElement("path", { d: "M4 12h12" }), React.createElement("path", { d: "M4 18h8" }));
    case "image":
      return React.createElement("svg", s, React.createElement("rect", { x: 3, y: 3, width: 18, height: 18, rx: 2 }), React.createElement("circle", { cx: 8.5, cy: 8.5, r: 1.5 }), React.createElement("polyline", { points: "21 15 16 10 5 21" }));
    case "group":
      return React.createElement("svg", s, React.createElement("rect", { x: 3, y: 3, width: 18, height: 18, rx: 2 }), React.createElement("path", { d: "M3 9h18" }), React.createElement("path", { d: "M9 3v18" }));
    case "shopping_list":
      return React.createElement("svg", s, React.createElement("path", { d: "M9 6h11" }), React.createElement("path", { d: "M9 12h11" }), React.createElement("path", { d: "M9 18h11" }), React.createElement("circle", { cx: 5, cy: 6, r: 1, fill: "currentColor" }), React.createElement("circle", { cx: 5, cy: 12, r: 1, fill: "currentColor" }), React.createElement("circle", { cx: 5, cy: 18, r: 1, fill: "currentColor" }));
    case "day":
      return React.createElement("svg", s, React.createElement("rect", { x: 3, y: 4, width: 18, height: 18, rx: 2 }), React.createElement("path", { d: "M16 2v4" }), React.createElement("path", { d: "M8 2v4" }), React.createElement("path", { d: "M3 10h18" }));
    case "card":
      return React.createElement("svg", s, React.createElement("rect", { x: 2, y: 3, width: 20, height: 18, rx: 2 }), React.createElement("path", { d: "M2 9h20" }));
    case "tip":
      return React.createElement("svg", s, React.createElement("circle", { cx: 12, cy: 12, r: 10 }), React.createElement("path", { d: "M12 16v-4" }), React.createElement("path", { d: "M12 8h.01" }));
    default:
      return React.createElement("svg", s, React.createElement("rect", { x: 3, y: 3, width: 18, height: 18, rx: 2 }));
  }
}
